<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { reactive, onMounted } from 'vue'
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import FormTextarea from '@/components/Elements/Forms/FormTextarea.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const backend = new Backend()
const formProcess = new FormProcess()
const gameStore = useGameStore()
const allianceStore = useAllianceStore()

// Define the validation schema
const schema = yup.object().shape({
  allianceDescription: yup.string().max(1024, () => t('modals.validation.maxChars', { max: 1024 }))
})

// Form values
const formValues = reactive<{ [key: string]: string }>({
  allianceDescription: ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  allianceDescription: ''
})

// Load current alliance data
onMounted(() => {
  const alliance = allianceStore.getAlliance(gameStore.player!.allianceId)
  if (alliance) {
    formValues.allianceDescription = alliance.description
  }
})

// Handle form submission
const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)

      backend
        .updateAllianceDescription(formValues.allianceDescription)
        .then(() => {
          gameStore.setTinySuccessToastMessage(t('modals.editAllianceDescription.messages.success'))
          gameStore.modalType = ''
        })
        .catch((error) => formProcess.processBackendErrorResponse(error, formErrors))
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}
</script>

<template>
  <SmallModel :title="t('modals.editAllianceDescription.title')">
    <form method="post" @submit.prevent="submit" class="p-3">
      <div class="mb-3">
        <FormTextarea
          v-model="formValues.allianceDescription"
          :label="t('modals.editAllianceDescription.label')"
          :rows="4"
          :error="formErrors.allianceDescription"
          :autoFocus="true"
        />
      </div>

      <div class="flex justify-between pt-2">
        <FormGenericError :error="formErrors.generic" />
        <ActionButton type="submit">{{ t('diplomacy.alliance.updateDescription') }}</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>

<style scoped lang="scss"></style> 